import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import useApiClient from "../hooks/useApiClient";
import type { Application } from "../types";

export type BulkDeleteResult = {
  deleted: number;
  ids: string[];
  notFoundIds: string[];
};

export const useApplicationsQuery = (tenantId: string) => {
  const { privateApiClient } = useApiClient();
  return useQuery<Application[], AxiosError>({
    queryFn: async () => (await privateApiClient.get(`/tenant/${tenantId}/applications`)).data,
    queryKey: ["applications", tenantId],
  });
};

export const useBulkDeleteApplicationsMutation = (tenantId: string) => {
  const { privateApiClient } = useApiClient();
  const qc = useQueryClient();
  return useMutation<BulkDeleteResult, AxiosError, { ids: string[] }>({
    mutationFn: async ({ ids }) =>
      (
        await privateApiClient.delete(
          `/admin/superadmin/tenant/${tenantId}/applications`,
          { data: { ids } },
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications", tenantId] }),
  });
};

export const useFormApplicationsQuery = (tenantId: string, formId: string) => {
  const { privateApiClient } = useApiClient();
  return useQuery<Application[], AxiosError>({
    queryFn: async () =>
      (await privateApiClient.get(`/tenant/${tenantId}/form/${formId}/applications`)).data,
    queryKey: ["applications", tenantId, formId],
  });
};

export const useUpdateApplicationStatusMutation = (tenantId: string) => {
  const { privateApiClient } = useApiClient();
  const qc = useQueryClient();
  return useMutation<void, AxiosError, { applicationId: string; status: string; adminNotes?: string | null }>({
    mutationFn: async ({ applicationId, status, adminNotes }) => {
      await privateApiClient.patch(`/tenant/${tenantId}/application/${applicationId}/status`, {
        status,
        adminNotes,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications", tenantId] }),
  });
};

export type Draft = {
  id: string;
  formId: string;
  email: string;
  parcelIds: string[];
  lastSavedAt: string;
  createdAt: string;
};

export const useDraftsQuery = (tenantId: string) => {
  const { privateApiClient } = useApiClient();
  return useQuery<Draft[], AxiosError>({
    queryFn: async () => (await privateApiClient.get(`/tenant/${tenantId}/drafts`)).data,
    queryKey: ["drafts", tenantId],
  });
};

export const useBulkDeleteDraftsMutation = (tenantId: string) => {
  const { privateApiClient } = useApiClient();
  const qc = useQueryClient();
  return useMutation<BulkDeleteResult, AxiosError, { ids: string[] }>({
    mutationFn: async ({ ids }) =>
      (
        await privateApiClient.delete(
          `/admin/superadmin/tenant/${tenantId}/drafts`,
          { data: { ids } },
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["drafts", tenantId] }),
  });
};

/**
 * Tworzy/upsertuje draft wniosku w momencie gdy użytkownik kliknął
 * "Złóż uwagę/wniosek" na publicznej mapie. Zapisuje do
 * `application_drafts` z parcelIds, IP i user-agent — admin widzi
 * w panelu, że ktoś rozpoczął procedurę składania wniosku, nawet
 * jeśli nie ukończył formularza.
 *
 * Używa `publicApiClient` bo użytkownik nie jest zalogowany. Backend
 * stosuje throttling (3 req/10s) — anti-spam.
 *
 * Zwracany draftId zachowujemy w sessionStorage. Po finalnym submit
 * formularza, mogą być powiązane (poza scope MVP — admin póki co
 * widzi drafty i wnioski jako osobne listy).
 */
export const useCreateApplicationDraftMutation = () => {
  const { publicApiClient } = useApiClient();
  return useMutation<
    { id: string; editToken?: string },
    AxiosError,
    { tenantId: string; formId: string; parcelIds: string[]; email?: string }
  >({
    mutationFn: async ({ tenantId, formId, parcelIds, email }) => {
      return (
        await publicApiClient.post(
          `/tenant/${tenantId}/form/${encodeURIComponent(formId)}/draft`,
          { parcelIds, email: email || undefined, payload: {} },
        )
      ).data;
    },
    mutationKey: ["create-application-draft"],
  });
};
